import { convertDataToHtml } from 'commonComponents/richText';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import {
  FormKeys as ServicesFormKeys,
  getCurrentDate,
  getDefaultServicesData,
  getServicesData,
  isTimeSameOrBefore,
  ServicesTimeError,
  serviceTypes,
  slugs,
  visitTypes,
} from 'src/app/components/salesComponents/contractCreation/addServices/helper';
import {
  dispatchOptionEnums,
  FormKeys as OnDemandSystemServiceFormKeys,
  MONTHLY_RATE as ON_DEMAND_MONTHLY_RATE,
  systemServicesConstant,
} from 'src/app/components/salesComponents/contractCreation/onDemandServices/systemService';
import {
  FormKeys,
  FormKeys as PaymentTermsFormKeys,
  PlanTypeEnums,
} from 'src/app/components/salesComponents/contractCreation/paymentTerms/helper';
import {
  getDateDifference,
  isObjectEmpty,
  removeKeysFromObject,
} from 'src/helper/utilityFunctions';
import { proposalTypeEnum, SelectedDateTpeContract, stageStatus } from 'src/utils/constants';
import { convertMMDDYYYYToDayJsDate, formatDayJsDate } from 'src/utils/passTime/time';

export const ActiveStepsKeys = {
  SERVICES: 'services',
  DEVICES: 'devices',
  ON_DEMAND_SERVICES: 'onDemandServices',
  PAYMENT_TERMS: 'paymentTerms',
  DESCRIPTIONS: 'descriptions',
  CONFIGURATION: 'configuration',
  SIGNEES: 'signees',
};

export const ActiveSteps = [...Object.values(ActiveStepsKeys)];

export const defaultSteps = [
  {
    name: 'Services',
    subtext: 'Add services of this proposal',
    value: ActiveStepsKeys.SERVICES,
    status: stageStatus.CURRENT,
  },
  {
    name: 'Devices',
    subtext: 'Choose devices for checkpoints',
    value: ActiveStepsKeys.DEVICES,
    status: stageStatus.PENDING,
  },
  {
    name: 'On Demand',
    subtext: 'Add services of this proposal',
    value: ActiveStepsKeys.ON_DEMAND_SERVICES,
    status: stageStatus.PENDING,
  },
  {
    name: 'Payment Terms',
    subtext: 'Set payment preferences',
    value: ActiveStepsKeys.PAYMENT_TERMS,
    status: stageStatus.PENDING,
  },
  {
    name: 'Description',
    subtext: 'Terms & conditions for proposals',
    value: ActiveStepsKeys.DESCRIPTIONS,
    status: stageStatus.PENDING,
  },
  {
    name: 'Signees',
    subtext: 'Add/choose signees for contract',
    value: ActiveStepsKeys.SIGNEES,
    status: stageStatus.PENDING,
  },
];

const getServicesPayload = (formData) => {
  return {
    name: formData.name,
    timezone: formData.timezone?.id,
    startDate: formatDayJsDate(formData.startDate, 'date'),
    endDate: formatDayJsDate(formData.endDate, 'date'),
    [ActiveStepsKeys.SERVICES]: formData[ActiveStepsKeys.SERVICES]?.map((service) => ({
      ...service,
      officerType: service.officerType?.id,
      lineItem: service?.lineItem?.value,
      instructions: convertDataToHtml(service?.instructions),
      hourlyRate: Number(service?.hourlyRate),
      pricePerHit: Number(service?.pricePerHit),
      visits: service.visits.map((visit) => ({
        ...visit,
        reqOfficers: Number(visit.reqOfficers),
        startTime: formatDayJsDate(visit?.startTime, 'time'),
        endTime: formatDayJsDate(visit?.endTime, 'time'),
      })),
      includeVehicle: service?.includeVehicle,
      ...(service?.includeVehicle
        ? {
            noOfVehicles: Number(service?.noOfVehicles),
            vehicleRate: Number(service?.vehicleRate),
          }
        : {}),
    })),
  };
};

const getDevicesPayload = (formData) => {
  return {
    [ActiveStepsKeys.DEVICES]: formData.map((device) => ({ ...device, price: device.price || 0 })),
  };
};

const getPaymentTermsPayload = (data) => {
  const payload = {
    ...data,
    planId: Number(data.plan),
    paymentMethodId: Number(data.paymentMethod.value),
    holidayGroupId: Number(data.holidayGroup.value),
    holidays: data?.holidayGroup?.holidays,
    paymentDateId: Number(data.paymentDate.value),
    billingTypeId: Number(data?.billingType.value),
    contractType: Number(data?.contractType?.value),
    fuelSurcharge: data.fuelSurcharge ? Number(data.fuelSurcharge) : null,
    taxRate: data.taxRate ? Number(data.taxRate) : null,
    flatRate: Number(data.flatRate),
    hasDifferentBillingAddress: data.hasDifferentBillingAddress,
    billingFrequency: Number(data.billingFrequency.value),
    // holidayGroupId: 6,
    address: {
      streetAddress: data.address,
      country: data.country,
      state: data.state,
      city: data.city,
      countryCode: data.countryCode,
      postalCode: data.postalCode,
    },
  };

  if (data.plan !== PlanTypeEnums.FLAT) delete payload.flatRate;

  if (data.plan === PlanTypeEnums.FLAT) payload.fuelSurcharge = 0;

  delete payload.billingType;
  delete payload.plan;
  delete payload.paymentMethod;
  delete payload.paymentDate;
  delete payload.holidayGroup;

  return { [ActiveStepsKeys.PAYMENT_TERMS]: payload };
};

const getOnDemandServicesPayload = (formData) => {
  return {
    [ActiveStepsKeys.ON_DEMAND_SERVICES]: formData.map((onDemandService) => ({
      ...onDemandService,
      price: Number(onDemandService.price),
      quantity: Number(onDemandService.quantity),
      dispatchRate: onDemandService?.dispatchRate?.value || null,
    })),
  };
};

const getDescriptionsPayload = (formData) => {
  return {
    [ActiveStepsKeys.DESCRIPTIONS]: { services: convertDataToHtml(formData?.services) },
  };
};

const getConfigurationPayload = (formData) => {
  return { [ActiveStepsKeys.CONFIGURATION]: [...formData[ActiveStepsKeys.SIGNEES]] };
};

export const getPayload = (formData, key, apiData) => {
  let payload = {};
  switch (key) {
    case ActiveStepsKeys.SERVICES:
      payload = getServicesPayload(formData);
      break;
    case ActiveStepsKeys.DEVICES:
      payload = getDevicesPayload(formData);
      break;
    case ActiveStepsKeys.PAYMENT_TERMS:
      payload = getPaymentTermsPayload(formData);
      break;
    case ActiveStepsKeys.ON_DEMAND_SERVICES:
      payload = getOnDemandServicesPayload(formData, apiData);
      break;
    case ActiveStepsKeys.DESCRIPTIONS:
      payload = getDescriptionsPayload(formData);
      break;
    case ActiveStepsKeys.SIGNEES:
      payload = getConfigurationPayload(formData);
      break;
  }
  return payload;
};

export const getServicesApiData = (apiData, baseRates, lineItems) => {
  const details = apiData?.details;

  const doesServicesExist = apiData?.[ActiveStepsKeys.SERVICES]?.length;

  const selectedDateType =
    details?.selectedDateType === SelectedDateTpeContract.oneTime ? 'endDate' : 'renewalDate';

  const startEndDates = details?.startDate
    ? {
        startDate: convertMMDDYYYYToDayJsDate(details?.startDate),
        endDate: convertMMDDYYYYToDayJsDate(details?.endDate),
      }
    : {};

  const actualContractDates =
    details?.actualContractDates && details?.actualContractDates?.start
      ? {
          startDate: convertMMDDYYYYToDayJsDate(
            dayjs(details?.actualContractDates?.start).format('YYYY/MM/DD'),
          ),
          endDate: convertMMDDYYYYToDayJsDate(
            dayjs(details?.actualContractDates?.end).format('YYYY/MM/DD'),
          ),
        }
      : {};

  const apiTimezone = apiData?.details?.timezone;
  const timezone = apiTimezone?.id ? { value: apiTimezone.id, label: apiTimezone.name } : null;

  if (!doesServicesExist)
    return {
      ...startEndDates,
      timezone,
      [ActiveStepsKeys.SERVICES]: [getDefaultServicesData(0)],
      selectedDateType,
      renewalReminderDays: selectedDateType === 'renewalDate' ? details?.renewalReminderDays : null,
      autoRenewal: selectedDateType === 'renewalDate' ? details?.autoRenewal : null,
      type: details?.type,
      actualContractDates,
      proposalType: details?.proposalType,
    };

  const services = apiData?.[ActiveStepsKeys.SERVICES];

  return {
    ...(details?.startDate
      ? {
          startDate: convertMMDDYYYYToDayJsDate(details?.startDate),
          endDate: convertMMDDYYYYToDayJsDate(details?.endDate),
        }
      : {}),
    timezone,
    [ActiveStepsKeys.SERVICES]: getServicesData(services, baseRates, lineItems),
    selectedDateType,
    renewalReminderDays: selectedDateType === 'renewalDate' ? details.renewalReminderDays : null,
    autoRenewal: selectedDateType === 'renewalDate' ? details?.autoRenewal : null,
    type: details?.type,
    actualContractDates,
    proposalType: details?.proposalType,
  };
};

const getConfigurationEditData = (formData, details) => {
  const { phoneNumber, fax, email, licenseNumber } = details;
  return {
    phoneNumber,
    fax,
    email,
    licenseNumber,
    [ActiveStepsKeys.SIGNEES]: formData,
  };
};

export const getEditData = (formData, key, apiData) => {
  const details = apiData?.details;

  if (key === ActiveStepsKeys.SIGNEES) return getConfigurationEditData(formData, details);

  return formData;
};

const emptyStateBaseRates = {
  [slugs.ARMED_OFFICER]: 0,
  [slugs.PATROL_OFFICER]: 0,
  [slugs.DEDICATED_OFFICER]: 0,
  [slugs.VISITOR_MANAGEMENT]: 0,
  [slugs.LOAD_MANAGEMENT]: 0,
};

export const useBaseRates = (preferences) => {
  const [baseRates, setBaseRates] = useState({ ...emptyStateBaseRates });
  useEffect(() => {
    if (preferences) calculateBaseRates();
  }, [preferences]);

  const calculateBaseRates = () => {
    let newBaseRates = {
      ...emptyStateBaseRates,
    };
    preferences?.generalServices?.forEach((preference) => {
      newBaseRates[preference.slug] = preference.rateValue;
    });
    preferences?.extraServices?.forEach((preference) => {
      newBaseRates[preference.slug] = preference.rateValue;
    });

    setBaseRates({ ...baseRates, ...newBaseRates });
  };

  return baseRates;
};

const getValidateServices = (formData, apiData, baseRates) => {
  const shouldValidateDates = formData?.startDate || formData?.endDate;

  return {
    timezone: formData.timezone,
    ...(shouldValidateDates
      ? {
          startDate: formatDayJsDate(formData?.startDate, 'date'),
          endDate: formatDayJsDate(formData?.endDate, 'date'),
        }
      : {}),
    [ActiveStepsKeys.SERVICES]: formData[ActiveStepsKeys.SERVICES].map((service) => {
      const visits = service.visits.map((visit) => {
        const updatedVisit = {
          ...visit,
          startTime: formatDayJsDate(visit.startTime || null, 'time'),
          endTime: formatDayJsDate(visit.endTime || null, 'time'),
        };

        if (updatedVisit.visitType === visitTypes.FIXED) delete updatedVisit.endTime;

        return updatedVisit;
      });

      return {
        ...service,
        officerType: !isObjectEmpty(service.officerType)
          ? { ...service.officerType, rate: baseRates?.[service.officerType.value] }
          : null,
        lineItem: service?.lineItem?.value || null,
        visits,
        ...(service?.type === serviceTypes.DEDICATED && service?.includeVehicle
          ? {
              noOfVehicles: service?.noOfVehicles,
              vehicleRate: service?.vehicleRate,
            }
          : {}),
      };
    }),
  };
};

const getValidateConfiguration = ({ phoneNumber, email, fax, licenseNumber }) => {
  return {
    ...(phoneNumber && { phoneNumber }),
    ...(email && { email }),
    ...(fax && { fax }),
    ...(licenseNumber && { licenseNumber }),
  };
};

const getValidatePaymentTerms = (formData) => {
  let paymentTerms = {
    ...formData,
    paymentMethod:
      formData?.paymentMethod?.value != null ? JSON.stringify(formData.paymentMethod.value) : '',
    holidayGroup:
      formData?.holidayGroup?.value != null ? JSON.stringify(formData.holidayGroup.value) : '',
    paymentDate:
      formData?.paymentDate?.value != null
        ? JSON.stringify(Number(formData.paymentDate.value))
        : '',
    billingType:
      formData?.billingType?.value != null ? JSON.stringify(formData.billingType.value) : '',
    contractType:
      formData?.contractType?.value != null ? JSON.stringify(formData.contractType.value) : '',
  };

  if (paymentTerms.plan !== PlanTypeEnums.FLAT)
    paymentTerms = removeKeysFromObject(paymentTerms, ['flatRate']);

  if (paymentTerms.plan === PlanTypeEnums.FLAT)
    paymentTerms = removeKeysFromObject(paymentTerms, ['fuelSurcharge']);

  if (!paymentTerms.hasDifferentBillingAddress)
    paymentTerms = removeKeysFromObject(paymentTerms, [
      FormKeys.ADDRESS,
      FormKeys.CITY,
      FormKeys.STATE,
      FormKeys.COUNTRY,
      FormKeys.COUNTRY_CODE,
      FormKeys.POSTAL_CODE,
    ]);

  return {
    [ActiveStepsKeys.PAYMENT_TERMS]: paymentTerms,
  };
};

const getValidatePayloadOnDemandServices = (formData, apiData) => {
  const onDemandServices = formData?.map((service) => {
    if (service?.title === systemServicesConstant.DISPATCH_REQUEST) {
      let dispatchRateValue = service?.dispatchRate?.value;
      if (dispatchRateValue === dispatchOptionEnums.nonBillable)
        service = removeKeysFromObject(service, [
          OnDemandSystemServiceFormKeys.price,
          OnDemandSystemServiceFormKeys.peakHoursPrice,
        ]);
      if (dispatchRateValue === dispatchOptionEnums.flatRate)
        service = removeKeysFromObject(service, [OnDemandSystemServiceFormKeys.peakHoursPrice]);
      if (apiData?.details?.proposalType === proposalTypeEnum.dispatch && !dispatchRateValue) {
        dispatchRateValue = '';
      }
      if (!service?.peakHoursPrice)
        service = removeKeysFromObject(service, [OnDemandSystemServiceFormKeys.peakHoursPrice]);
      return {
        ...service,
        dispatchRate: dispatchRateValue,
      };
    }
    return { ...service };
  });
  return {
    [ActiveStepsKeys.ON_DEMAND_SERVICES]: onDemandServices,
  };
};

export const getValidateFormData = (formData, activeTabKey, apiData) => {
  if (activeTabKey === ActiveStepsKeys.SERVICES)
    return { ...getValidateServices(formData, apiData) };
  if (activeTabKey === ActiveStepsKeys.ON_DEMAND_SERVICES)
    return getValidatePayloadOnDemandServices(formData, apiData);
  if (activeTabKey === ActiveStepsKeys.SIGNEES) return getValidateConfiguration(formData);
  if (activeTabKey === ActiveStepsKeys.PAYMENT_TERMS) return getValidatePaymentTerms(formData);

  return { [activeTabKey]: formData };
};

export const getViewDisabledContractClass = (isPublished) =>
  isPublished ? 'viewDisabledContract' : '';

const formatMinutes = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return mins === 1 ? `${mins} min` : `${mins} mins`;
  } else if (mins === 0) {
    return hours === 1 ? `${hours} hour` : `${hours} hours`;
  } else {
    const hourString = hours === 1 ? `${hours} hour` : `${hours} hours`;
    const minString = mins === 1 ? `${mins} min` : `${mins} mins`;
    return `${hourString} and ${minString}`;
  }
};

export const getServicesTimeFieldErrors = (services) => {
  const errors = {};
  const currentDate = getCurrentDate();

  services.forEach((service, serviceIndex) => {
    service.visits.forEach((visit, visitIndex) => {
      const sTime = visit[ServicesFormKeys.START_TIME];
      const eTime = visit[ServicesFormKeys.END_TIME];

      // Convert start and end times to dayjs objects
      const start = currentDate.set('hour', sTime?.hour()).set('minute', sTime?.minute());
      let end = currentDate.set('hour', eTime?.hour()).set('minute', eTime?.minute());

      if (isTimeSameOrBefore(end, start)) end = end.add('1', 'd');

      const minutesDiff = end.diff(start, 'minute');

      if (
        service[ServicesFormKeys.TYPE] === serviceTypes.PATROL &&
        visit[ServicesFormKeys.VISIT_TYPE] === visitTypes.FIXED
      ) {
        return;
      }

      if (service[ServicesFormKeys.TYPE] === serviceTypes.DEDICATED && minutesDiff < 30) {
        errors[
          `${ActiveStepsKeys.SERVICES},${serviceIndex},${ServicesFormKeys.VISITS},${visitIndex},${ServicesTimeError}`
        ] = 'Start time and end time duration should be at least 30 mins';
        return;
      }

      const minVisitTimeRange = Number(visit[ServicesFormKeys.NUMBER_OF_VISITS]) * 15;

      if (
        service[ServicesFormKeys.TYPE] === serviceTypes.PATROL &&
        minutesDiff < minVisitTimeRange
      ) {
        errors[
          `${ActiveStepsKeys.SERVICES},${serviceIndex},${ServicesFormKeys.VISITS},${visitIndex},${ServicesTimeError}`
        ] =
          `Start time and end time duration should be at least ${formatMinutes(minVisitTimeRange)}`;
        return;
      }
    });
  });
  return Object.keys(errors).length ? errors : null;
};

export const getErrorKey = ({ activeStep, index, key }) => {
  return `${activeStep},${index},${key}`;
};

export const showError = ({ activeStep, index, key, errorMessages }) => {
  return errorMessages?.[`${getErrorKey({ activeStep, index, key })}`];
};
export const getNestedErrorKey = ({ activeStep, index, nestedFormDataKey, nestedIndex, key }) => {
  return `${activeStep},${index},${nestedFormDataKey},${nestedIndex},${key}`;
};

export const showNestedError = ({
  activeStep,
  index,
  nestedFormDataKey,
  nestedIndex,
  key,
  errorMessages,
}) => {
  return errorMessages?.[
    `${getNestedErrorKey({ activeStep, index, nestedFormDataKey, nestedIndex, key })}`
  ];
};

export const getPlanId = (apiData, formData) => {
  /**
   * If StartDate does not exist that means dates are to be decided later
   * so we create a default plan of weekly payments
   * */
  const daysDifference = apiData?.details?.startDate
    ? getDateDifference(apiData?.details?.startDate, apiData?.details?.endDate)
    : 7;
  if (formData?.plan?.id?.toString()) {
    return formData?.plan?.id?.toString()
      ? formData?.plan
      : daysDifference < 7
        ? PlanTypeEnums.EVENT
        : PlanTypeEnums.WEEKLY || PlanTypeEnums.WEEKLY;
  }
  let apiPlanId = apiData?.details?.plan;
  apiPlanId = Number.isInteger(apiPlanId) ? apiPlanId.toString() : apiPlanId;

  if (!apiPlanId) {
    return daysDifference < 7 ? PlanTypeEnums.EVENT : PlanTypeEnums.WEEKLY;
  }

  return apiPlanId;
};

export const getOnDemandTotal = (onDemandServices) => {
  if (!onDemandServices) return 0;
  return onDemandServices?.reduce((acc, service) => {
    if (service.rate === ON_DEMAND_MONTHLY_RATE && !service._destroy) {
      if (service?.title === systemServicesConstant.DISPATCH_REQUEST) {
        // return service?.dispatchRate?.value === 'chargePerAlarm' ||
        //   service?.dispatchRate?.value === 'nonBillable'
        //   ? 0
        //   : acc + Number(service.price) * Number(service.quantity);
        return service?.dispatchRate?.value === dispatchOptionEnums.flatRate
          ? acc + Number(service.price) * Number(1)
          : acc;
      }

      return acc + Number(service.price) * Number(service.quantity);
    }
    return acc;
  }, 0);
};

export const PlanMultiplier = {
  [PlanTypeEnums.MONTHLY]: 30.42 / 7,
  [PlanTypeEnums.BI_WEEKLY]: 2,
  [PlanTypeEnums.WEEKLY]: 1,
  [PlanTypeEnums.EVENT]: 1,
  [PlanTypeEnums.FLAT]: 1,
};

export const PlanDivider = {
  [PlanTypeEnums.MONTHLY]: 1,
  [PlanTypeEnums.BI_WEEKLY]: 2,
  [PlanTypeEnums.WEEKLY]: 30.42 / 7,
  [PlanTypeEnums.EVENT]: 1,
  [PlanTypeEnums.FLAT]: 1,
};

/**
 * Calculates contract totals for different plan types (weekly, monthly, flat, etc.)
 * by including services, on-demand services, fuel surcharge, and tax.
 *
 * @param {Object} params
 * @param {Object} params.paymentTerms - Contains tax rate, flat rate, and fuel surcharge.
 * @param {Array} params.services - List of recurring services with calculations.
 * @param {number} params.servicesTotal - Weekly total of recurring services.
 * @param {Array} params.onDemandServices - List of one-time/on-demand services.
 *
 * @returns {Object} calculations - Breakdown of fuel surcharge, tax, and total for each plan type.
 *
 * Example:
 * --------
 * const result = getContractCalculations({
 *   paymentTerms: {
 *     TAX_RATE: 10,       // 10% tax
 *     FLAT_RATE: 2000,    // Flat plan cost
 *     FUEL_SURCHARGE: 5,  // 5% fuel surcharge
 *   },
 *   services: [
 *     {
 *       addFuelSurcharge: true,
 *       calculations: { total: 1000 } // Weekly service total
 *     },
 *   ],
 *   servicesTotal: 1000, // Weekly total of recurring services
 *   onDemandServices: [
 *     { title: 'Dispatch Request', price: 100, quantity: 2, dispatchRate: { value: 'flatRate' } },
 *     { title: 'Cleaning', price: 50, quantity: 1, rate: 'MONTHLY', _destroy: false }
 *   ]
 * });
 *
 * console.log(result);
 *  {
 *    WEEKLY: { fuelSurchargeAmount: 50, taxAmount: 105, total: 1155 },
 *    MONTHLY: { ... },
 *    FLAT: { ... }
 *  }
 */
export const getContractCalculations = ({
  paymentTerms,
  services,
  // servicesTotal,
  // onDemandTotal,
  onDemandServices,
}) => {
  // Extract relevant payment term values
  const taxRate = paymentTerms?.[PaymentTermsFormKeys.TAX_RATE];

  const flatRate = paymentTerms?.[PaymentTermsFormKeys.FLAT_RATE];

  const fuelSurcharge = paymentTerms?.[PaymentTermsFormKeys.FUEL_SURCHARGE];

  const calculations = {};

  // We will take weekly as base
  const servicesTotal = services?.reduce(
    (acc, service) => acc + Number(service?.calculations?.[PlanTypeEnums.WEEKLY]?.total || 0),
    0,
  );

  // Loop through each plan type (e.g., WEEKLY, MONTHLY, FLAT)
  Object.values(PlanTypeEnums).forEach((planTypeValue) => {
    let fuelSurchargeAmount = 0;

    // 🔹 1. Calculate fuel surcharge for services that have "addFuelSurcharge" enabled
    if (fuelSurcharge) {
      services?.forEach((service) => {
        if (service.addFuelSurcharge) {
          fuelSurchargeAmount +=
            (Number(fuelSurcharge) / 100) *
            service.calculations.total *
            PlanMultiplier[planTypeValue];
        }
      });
    }

    // 🔹 2. Calculate per-day vehicle expense & weekly expense for each service
    // ? No need to calculate vehicle rate separately as it is already added in service total
    // services?.forEach((service) => {
    //   if (
    //     service?.type === serviceTypes.DEDICATED &&
    //     service?.visits?.[0].dutyDays?.length &&
    //     service.includeVehicle &&
    //     service.vehicleRate &&
    //     service.noOfVehicles
    //   ) {
    //     const start = dayjs(service?.visits?.[0]?.startTime);
    //     const end = dayjs(service?.visits?.[0]?.endTime);

    //     const serviceHours = end.diff(start, 'hour', true);

    //     if (serviceHours) {
    //       const perDayVehicleExpense =
    //         Number(serviceHours) *
    //         parseFloat(service.vehicleRate) *
    //         parseInt(service.noOfVehicles, 10);

    //       const amountPerWeek = perDayVehicleExpense * 7.0;

    //       // Scale weekly expense by current plan multiplier
    //       vehicleExpenseTotal += amountPerWeek * PlanMultiplier[planTypeValue];
    //     }
    //   }
    // });

    // 🔹 3. Services total (base recurring services)
    // servicesTotal is always in weekly terms → scale with PlanMultiplier
    const servicesPlanTotal = servicesTotal * PlanMultiplier[planTypeValue];

    // 🔹 4. Dispatch service total (special handling: only flat-rate dispatch requests are counted)
    const dispatchServiceTotal = onDemandServices?.reduce((acc, service) => {
      if (service?.title === systemServicesConstant.DISPATCH_REQUEST) {
        return service?.dispatchRate?.value === dispatchOptionEnums.flatRate
          ? acc + Number(service.price) * Number(service.quantity)
          : acc;
      }
      return acc;
    }, 0);

    // 🔹 5. Other on-demand services total (monthly services that aren’t destroyed)
    const otherOnDemandServicesTotal = onDemandServices?.reduce((acc, service) => {
      if (
        service?.title !== systemServicesConstant.DISPATCH_REQUEST &&
        service.rate === ON_DEMAND_MONTHLY_RATE &&
        !service._destroy
      ) {
        return acc + Number(service.price) * Number(service.quantity);
      }
      return acc;
    }, 0);

    // 🔹 6. Scale dispatch services (dispatch is weekly-based)
    const dispatchPlanTotal = dispatchServiceTotal * PlanMultiplier[planTypeValue];

    // 🔹 7. Scale on-demand services (onDemand is monthly → convert to weekly/annual/etc.)
    const onDemandPlanTotal = otherOnDemandServicesTotal / PlanDivider[planTypeValue];

    // 🔹 8. Add everything together
    let totalAfterFuelSurcharge =
      servicesPlanTotal + dispatchPlanTotal + onDemandPlanTotal + fuelSurchargeAmount;

    // If flat plan → ignore breakdown and just use flat rate
    if (planTypeValue === PlanTypeEnums.FLAT) totalAfterFuelSurcharge = Number(flatRate);

    // 🔹 9. Apply tax if applicable
    const taxAmount = Number(taxRate) ? (Number(taxRate) / 100) * totalAfterFuelSurcharge : 0;

    // 🔹 10. Final total
    const total = totalAfterFuelSurcharge + taxAmount;

    // 🔹 11. Store results for this plan type
    calculations[planTypeValue] = {
      fuelSurchargeAmount,
      // vehicleExpenseTotal: 0,
      taxAmount,
      total,
    };
  });

  return calculations;
};

export const getTotalPrice = ({ planId, contractCalculations }) => {
  planId = Number.isInteger(planId) ? planId.toString() : planId;

  return Number(contractCalculations?.[planId]?.total || 0);
};
